import AsyncStorage from '@react-native-async-storage/async-storage';
import { functionsEu, httpsCallable } from '../../firebase';

// Partner reviews. Reviews and each partner's aggregate are read straight
// from Firestore (partnerReviews / partnerReviewStats — public reads) and
// written only through the review Cloud Functions (functions/partnerReviews.js).

export const REVIEW_MAX_LENGTH = 1000;
export const REPORT_DETAILS_MAX_LENGTH = 500;
export const REVIEW_REPORT_REASONS = ['spam', 'harassment', 'inappropriate', 'fake', 'other'] as const;
export type ReviewReportReason = typeof REVIEW_REPORT_REASONS[number];
export type ReviewModerationAction = 'hide' | 'restore' | 'remove' | 'dismiss';
export type StarValue = 1 | 2 | 3 | 4 | 5;

export type PartnerReview = {
  id: string;
  venueId: string;
  reviewerUid: string;
  reviewerName: string;
  reviewerPhotoUrl: string;
  rating: number;
  text: string;
  status: 'published' | 'hidden' | 'removed';
  createdAt: number | null;
  editedAt: number | null;
};

export type PartnerReviewStats = {
  count: number;
  average: number;
  distribution: Record<StarValue, number>;
};

export const EMPTY_REVIEW_STATS: PartnerReviewStats = { count: 0, average: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };

export const reviewIdFor = (venueId: string, uid: string) => `${venueId}_${uid}`;

const toMillis = (value: unknown): number | null => {
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') return (value as { toMillis: () => number }).toMillis();
  return typeof value === 'number' ? value : null;
};

export function normalizeReview(id: string, data: Record<string, unknown>): PartnerReview {
  return {
    id,
    venueId: String(data.venueId || ''),
    reviewerUid: String(data.reviewerUid || ''),
    reviewerName: String(data.reviewerName || ''),
    reviewerPhotoUrl: typeof data.reviewerPhotoUrl === 'string' ? data.reviewerPhotoUrl : '',
    rating: Number(data.rating) || 0,
    text: typeof data.text === 'string' ? data.text : '',
    status: data.status === 'hidden' || data.status === 'removed' ? data.status : 'published',
    createdAt: toMillis(data.createdAt),
    editedAt: toMillis(data.editedAt),
  };
}

export function normalizeStats(data: Record<string, unknown> | undefined | null): PartnerReviewStats {
  if (!data) return EMPTY_REVIEW_STATS;
  const raw = (data.distribution || {}) as Record<string, unknown>;
  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<StarValue, number>;
  ([1, 2, 3, 4, 5] as StarValue[]).forEach(star => { distribution[star] = Math.max(0, Number(raw[star]) || 0); });
  const count = Math.max(0, Number(data.count) || 0);
  const sum = Math.max(0, Number(data.sum) || 0);
  return { count, average: count ? sum / count : 0, distribution };
}

const call = <Req, Res>(name: string) => httpsCallable<Req, Res>(functionsEu, name);

export async function savePartnerReview(venueId: string, rating: number, text: string) {
  const { data } = await call<{ venueId: string; rating: number; text: string }, { created: boolean; review: Record<string, unknown> }>('upsertPartnerReviewEu')({ venueId, rating, text });
  return { created: data.created, review: normalizeReview(String(data.review.id), data.review) };
}

export async function deletePartnerReview(venueId: string) {
  const { data } = await call<{ venueId: string }, { deleted: boolean }>('deletePartnerReviewEu')({ venueId });
  return data;
}

export async function reportPartnerReview(reviewId: string, reason: ReviewReportReason, details: string) {
  await call<{ reviewId: string; reason: string; details: string }, { reported: boolean }>('reportPartnerReviewEu')({ reviewId, reason, details });
}

export async function moderatePartnerReview(reviewId: string, action: ReviewModerationAction, reason = '') {
  const { data } = await call<{ reviewId: string; action: string; reason: string }, { status: string | null; reportsResolved: number }>('moderatePartnerReviewEu')({ reviewId, action, reason });
  return data;
}

// The server's `details.reason` (functions/partnerReviews.js) → i18n key.
export function reviewErrorKey(error: unknown): string {
  const err = error as { code?: string; details?: { reason?: string } } | null;
  const reason = err?.details?.reason;
  const known: Record<string, string> = {
    'sign-in': 'partnerReviews.errors.signIn',
    'verify-email': 'partnerReviews.errors.verifyEmail',
    'own-profile': 'partnerReviews.errors.ownProfile',
    removed: 'partnerReviews.errors.removed',
    rating: 'partnerReviews.errors.rating',
    'text-length': 'partnerReviews.errors.tooLong',
    venue: 'partnerReviews.errors.unavailable',
    review: 'partnerReviews.errors.reviewGone',
    'own-review': 'partnerReviews.errors.ownReview',
    access: 'partnerReviews.errors.access',
    profile: 'partnerReviews.errors.access',
  };
  if (reason && known[reason]) return known[reason];
  const code = String(err?.code || '').replace(/^functions\//, '');
  if (code === 'unauthenticated') return 'partnerReviews.errors.signIn';
  if (code === 'unavailable' || code === 'deadline-exceeded' || code === 'internal') return 'partnerReviews.errors.network';
  return 'partnerReviews.errors.generic';
}

// A review the user started but hasn't submitted (e.g. sign-in or email
// verification got in the way). Kept per partner until submitted/discarded.
export type ReviewDraft = { rating: number; text: string };
const draftKey = (venueId: string) => `@tonyte/partner-review-draft/${venueId}`;

export async function loadReviewDraft(venueId: string): Promise<ReviewDraft | null> {
  try {
    const raw = await AsyncStorage.getItem(draftKey(venueId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ReviewDraft>;
    const rating = Number.isInteger(parsed.rating) && Number(parsed.rating) >= 1 && Number(parsed.rating) <= 5 ? Number(parsed.rating) : 0;
    const text = typeof parsed.text === 'string' ? parsed.text.slice(0, REVIEW_MAX_LENGTH) : '';
    return rating || text ? { rating, text } : null;
  } catch {
    return null;
  }
}

export async function saveReviewDraft(venueId: string, draft: ReviewDraft) {
  try {
    if (!draft.rating && !draft.text.trim()) await AsyncStorage.removeItem(draftKey(venueId));
    else await AsyncStorage.setItem(draftKey(venueId), JSON.stringify(draft));
  } catch {
    // A lost draft is an inconvenience, never an error worth surfacing.
  }
}

export async function clearReviewDraft(venueId: string) {
  try { await AsyncStorage.removeItem(draftKey(venueId)); } catch { /* see saveReviewDraft */ }
}
